import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ClipboardCheck, ClipboardEdit } from "lucide-react";
import type { StatefulDiaryT } from "../../../types";
import { usePreferencesQuery } from "../../knock/api/preferencesQuery";
import { useFormsToDo } from "../features/new/hooks/useFormsToDo";

interface Props {
	diary: StatefulDiaryT;
}

const DiaryProgressMessage = ({ diary }: Props) => {
	const { data: preferences } = usePreferencesQuery();
	const { isUpToDate, itemCount, taskCount } = useFormsToDo(
		diary?.trackers_view || [],
		diary?.observations_view || [],
	);

	const getNotificationMessage = () => {
		if (!preferences) {
			return "You'll get an email and text when your next entry is due.";
		}

		const hasEmail = preferences.channel_types?.email;
		const hasSMS = preferences.channel_types?.sms;

		if (!hasEmail && !hasSMS) {
			return "You won't get reminders. Consider enabling email or text notifications in your settings.";
		}

		if (hasEmail && hasSMS) {
			return "You'll get an email and text when your next entry is due.";
		}

		return `You'll get an ${
			hasEmail ? "email" : "text"
		} when your next entry is due.`;
	};

	const getProgressMessage = () => {
		if (isUpToDate) {
			return `Great job! All your forms are up to date. ${getNotificationMessage()}`;
		}

		const formText = itemCount === 1 ? "form" : "forms";
		const entryText = taskCount === 1 ? "entry due" : "entries due";
		const needsText = taskCount === 1 ? "needs" : "need";

		return `You have ${itemCount} ${formText} with ${taskCount} ${entryText}.`;
	};

	return (
		<Alert className="gap-x-4 bg-gradient-to-br from-violet-50 dark:from-violet-950/50 via-purple-50 dark:via-purple-950/50 to-fuchsia-50 dark:to-fuchsia-950/50 shadow-lg border-2 border-violet-300/80 dark:border-violet-800/80 border-dashed">
			{isUpToDate ? (
				<ClipboardCheck className="stroke-violet-600 dark:stroke-violet-400 mt-1 mr-1" />
			) : (
				<ClipboardEdit className="stroke-fuchsia-600 dark:stroke-fuchsia-400 mt-1 mr-1" />
			)}
			<AlertTitle className="font-semibold text-violet-900 dark:text-violet-100 text-lg">
				{isUpToDate ? "All Caught Up" : "Forms Need Attention"}
			</AlertTitle>
			<AlertDescription className="text-md text-violet-800/90 dark:text-violet-200/90">
				{getProgressMessage()}
			</AlertDescription>
		</Alert>
	);
};

export default DiaryProgressMessage;
